'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import { useCountdown } from '@/hooks/useCountdown';
import { cn } from '@/utils/cn';

type CountdownProps = {
  /**
   * The target date for the countdown. This could be a Date object,
   * or a string in ISO 8601 format (e.g. "2025-01-31T00:00:00Z").
   */
  targetDate: string | Date;

  /**
   * Interval in milliseconds at which the countdown updates.
   * Defaults to 1 second (1000 ms).
   */
  intervalMs?: number;

  /**
   * Optional callback to handle "expired" case (if targetDate is in the past).
   * If provided, the countdown stops once the date has passed.
   */
  onExpire?: () => void;

  expiredElement?: ReactNode;

  suffix?: ReactNode;
} & Omit<HTMLAttributes<HTMLTimeElement>, 'children' | 'dateTime'>;

export const Countdown = ({
  targetDate,
  intervalMs = 1000,
  onExpire,
  className,
  expiredElement,
  suffix,
  ...props
}: CountdownProps) => {
  const { timeRemaining, isExpired } = useCountdown({
    targetDate,
    intervalMs,
    onExpire,
  });

  if (timeRemaining === null) {
    return (
      <span className="inline-flex h-4 w-40 animate-pulse rounded-lg bg-gray-700/80">
        <span className="sr-only">Loading...</span>
      </span>
    );
  }

  if (isExpired) {
    return expiredElement ?? <span>Betting closed</span>;
  }

  return (
    <time
      className={cn('text-gray', className)}
      dateTime={timeRemaining}
      {...props}
    >
      <strong className="font-semibold text-white">{timeRemaining}</strong>{' '}
      {suffix}
    </time>
  );
};
